// @owners { team: patients }
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { setShipmentPaymentMethods } from '~shared/actions/shipmentPaymentMethods';
import getIsAuthorized from '~shared/features/auth/selectors/getIsAuthorized';
import { type PaymentMethodWithAmount } from '~shared/features/payments/types';
import { staleTimes } from '~shared/queries/constants';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { shipmentPaymentMethods } from '~shared/queries/query-keys/shipmentPaymentMethods';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type PaymentMethod } from '~shared/types';

/**
 * Fetch all shipment payment methods for a patient.
 * Runs only when authorized; `options.enabled` is AND-ed with auth (defaults to true when omitted).
 * @returns Array of shipment payment methods
 */
export const useGetShipmentPaymentMethods = (options?: { enabled?: boolean }) => {
  const isAuthorized = useSelectorShared(getIsAuthorized);
  const enabled = isAuthorized && (options?.enabled ?? true);
  const { data, isLoading, isSuccess, error } = useQuery({
    ...shipmentPaymentMethods.fetchAll(),
    enabled,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });

  return {
    error,
    isLoading,
    isSuccess,
    shipmentPaymentMethods: data ?? [],
  };
};

/**
 * Bridge: mirror the React Query shipment-payment-methods data into the Redux
 * `shipments.shipmentPaymentMethods` slice so existing Reselect selectors
 * (checkout's getOrder, SchedulingCard, OrderBundling…) keep working without a
 * Redux networking action. One-directional (RQ → Redux). Mount once at an
 * always-rendered authed root. The bridge is the transitional dual-write step toward
 * strategy A (selectors read RQ directly, slice deleted) — see the
 * redux-thunk-to-react-query-migration skill (.cursor/skills/).
 *
 * Note: the sync runs in an effect (after paint), so there's a one-render window
 * where React Query has fresh data but the Redux slice hasn't been written yet —
 * selectors reading the slice (e.g. getOrder) can briefly see the previous value.
 * This is the inherent tradeoff of the bridge.
 */
export const useShipmentPaymentMethodsReduxBridge = () => {
  const dispatch = useDispatchShared();
  const { shipmentPaymentMethods, isSuccess } = useGetShipmentPaymentMethods();

  useEffect(() => {
    if (isSuccess) {
      dispatch(setShipmentPaymentMethods(shipmentPaymentMethods));
    }
  }, [dispatch, isSuccess, shipmentPaymentMethods]);
};

export const useGetShipmentPaymentMethodsWithDetails = ({ shipmentID }: { shipmentID: number | undefined }) => {
  const { paymentMethods } = useGetPaymentMethods();
  const { shipmentPaymentMethods } = useGetShipmentPaymentMethods();

  const paymentMethodsByID = paymentMethods.reduce<Record<number, PaymentMethod>>((methods, method) => {
    methods[method.id] = method;
    return methods;
  }, {});

  return shipmentPaymentMethods
    .filter((spm) => spm.shipment_id === shipmentID)
    .map((method) => {
      const { payment_method_id } = method;
      const paymentMethod: PaymentMethodWithAmount = {
        ...method,
        paymentMethod: paymentMethodsByID[payment_method_id] || undefined,
      };
      return paymentMethod;
    });
};
